import { calculatePay } from "@crewledger/shared/pay-rules";
import type { PayRule } from "@crewledger/shared/pay-rules";
import {
  buildRowWarnings,
  countRowExceptions,
  type RowWarningEquipmentMeta,
} from "@crewledger/shared/daily-entry/row-warnings";
import type {
  DailyEntryFlags,
  DailyEntryOptions,
  DailyEntryRowInput,
  DailyEntrySummary,
  GeofenceStatus,
} from "@crewledger/shared/schemas";

type DailyEntryEquipmentOption = DailyEntryOptions["equipment"][number];

export type DailyEntryUiRow = DailyEntryRowInput & {
  clientId: string;
  employeeId: string;
  workerName: string;
  crew: string | null;
  geofenceStatus?: GeofenceStatus | null;
};

export function createEmptyRow(worker: DailyEntryOptions["workers"][number]): DailyEntryUiRow {
  return {
    clientId: crypto.randomUUID(),
    id: undefined,
    workerId: worker.id,
    employeeId: worker.employeeId,
    workerName: worker.displayName,
    crew: worker.crew,
    hours: 8,
    jobCode: "",
    costCode: "",
    notes: "",
    flags: defaultFlags(),
    equipment: [],
  };
}

export function defaultFlags(): DailyEntryFlags {
  return {
    isWeekend: false,
    isHoliday: false,
    isNightShift: false,
    isHazard: false,
    isTraining: false,
    isManualOvertime: false,
    isManualDoubleTime: false,
  };
}

function mapPayRules(options: DailyEntryOptions): PayRule[] {
  return options.payRules.map((rule) => ({
    id: rule.id,
    type: rule.type as PayRule["type"],
    thresholdHours: rule.thresholdHours,
    multiplier: rule.multiplier,
    flatAmount: rule.flatAmount,
    percentagePremium: rule.percentagePremium,
    priority: rule.priority,
    active: rule.active,
  }));
}

function resolveWorker(
  options: DailyEntryOptions,
  workerId: string,
): DailyEntryOptions["workers"][number] | undefined {
  return options.workersById[workerId] ?? options.workers.find((w) => w.id === workerId);
}

function resolveWorkerPayRules(
  options: DailyEntryOptions,
  worker: DailyEntryOptions["workers"][number] | undefined,
) {
  const profileId = worker?.ruleProfileId ?? options.payRuleProfile.id;
  const bundle = options.payRuleProfilesById[profileId];
  if (bundle) {
    return {
      stackingMode: bundle.stackingMode,
      rules: bundle.rules.map((rule) => ({
        id: rule.id,
        type: rule.type as PayRule["type"],
        thresholdHours: rule.thresholdHours,
        multiplier: rule.multiplier,
        flatAmount: rule.flatAmount,
        percentagePremium: rule.percentagePremium,
        priority: rule.priority,
        active: rule.active,
      })),
    };
  }
  return {
    stackingMode: options.payRuleProfile.stackingMode,
    rules: mapPayRules(options),
  };
}

function mapBreakdownForPreview(
  breakdown: ReturnType<typeof calculatePay>["calculationBreakdown"],
  equipment: DailyEntryOptions["equipment"],
  rowEquipment: DailyEntryUiRow["equipment"],
) {
  let equipmentIndex = 0;
  return breakdown.map((line) => {
    if (line.category !== "equipment") {
      return { ...line, isSiteCost: false };
    }
    const usage = rowEquipment.filter((e) => e.usageHours > 0)[equipmentIndex];
    equipmentIndex += 1;
    const equip = equipment.find((item) => item.id === usage?.equipmentId);
    return {
      ...line,
      description: equip ? `${equip.name} (${equip.equipmentId})` : line.description,
      isSiteCost: true,
    };
  });
}

function applyManualOverrides(
  result: ReturnType<typeof calculatePay>,
  flags: DailyEntryFlags,
  hours: number,
  baseRate: number,
) {
  if (flags.isManualDoubleTime && hours > 0) {
    const doubleTimePay = Math.round(hours * baseRate * 2 * 100) / 100;
    return { ...result, regularHours: 0, overtimeHours: 0, doubleTimeHours: hours, regularPay: 0, overtimePay: 0, doubleTimePay, grossPay: doubleTimePay + result.premiumPay };
  }
  if (flags.isManualOvertime && hours > 0) {
    const overtimePay = Math.round(hours * baseRate * 1.5 * 100) / 100;
    return { ...result, regularHours: 0, overtimeHours: hours, doubleTimeHours: 0, regularPay: 0, overtimePay, doubleTimePay: 0, grossPay: overtimePay + result.premiumPay };
  }
  return result;
}

export function previewRowPay(
  row: DailyEntryUiRow,
  options: DailyEntryOptions,
  date: string,
  canViewPayroll: boolean,
) {
  const worker = resolveWorker(options, row.workerId);
  const baseRate = worker?.baseHourlyRate ?? 0;
  const payRules = resolveWorkerPayRules(options, worker);
  const equipmentUsage = row.equipment
    .filter((e) => e.usageHours > 0)
    .map((e) => {
      const equip = options.equipment.find((item) => item.id === e.equipmentId);
      return {
        equipmentId: e.equipmentId,
        usageHours: e.usageHours,
        hourlyCost: equip?.hourlyCost ?? 0,
      };
    });

  const calc = applyManualOverrides(
    calculatePay({
      baseHourlyRate: baseRate,
      date,
      hoursWorked: row.hours,
      cumulativeWeeklyHoursBefore: options.cumulativeWeeklyHours[row.workerId] ?? 0,
      flags: {
        isWeekend: row.flags.isWeekend,
        isHoliday: row.flags.isHoliday,
        isNightShift: row.flags.isNightShift,
        isHazard: row.flags.isHazard,
        isTraining: row.flags.isTraining,
      },
      profile: { stackingMode: payRules.stackingMode },
      rules: payRules.rules,
      equipmentUsage: equipmentUsage.map((e) => ({
        equipmentId: e.equipmentId,
        usageHours: e.usageHours,
        hourlyCost: e.hourlyCost,
      })),
    }),
    row.flags,
    row.hours,
    baseRate,
  );

  return {
    regularHours: calc.regularHours,
    overtimeHours: calc.overtimeHours,
    doubleTimeHours: calc.doubleTimeHours,
    holidayHours: calc.holidayHours,
    premiumHours: calc.premiumHours,
    grossPay: canViewPayroll ? calc.grossPay : null,
    equipmentCost: calc.equipmentCost,
    payWarnings: calc.warnings,
    breakdown: mapBreakdownForPreview(calc.calculationBreakdown, options.equipment, row.equipment),
  };
}

export function buildRowWarningsForRow(row: DailyEntryUiRow, options: DailyEntryOptions) {
  const equipmentById = Object.fromEntries(
    options.equipment.map(
      (item) =>
        [
          item.id,
          {
            id: item.id,
            equipmentId: item.equipmentId,
            name: item.name,
            status: item.status,
            siteId: item.siteId,
            requiredCertification: item.requiredCertification,
          } satisfies RowWarningEquipmentMeta,
        ] as const,
    ),
  );

  return buildRowWarnings(row, row.workerName, {
    siteId: options.siteId,
    equipmentById,
    workerCertifications: options.workerCertificationsByWorkerId[row.workerId] ?? [],
  });
}

export function summarizeRows(
  rows: DailyEntryUiRow[],
  options: DailyEntryOptions,
  date: string,
  canViewPayroll: boolean,
): DailyEntrySummary {
  const previews = rows.map((row) => previewRowPay(row, options, date, canViewPayroll));
  return {
    workerCount: rows.length,
    totalHours: rows.reduce((acc, row) => acc + row.hours, 0),
    regularHours: previews.reduce((acc, p) => acc + p.regularHours, 0),
    overtimeHours: previews.reduce((acc, p) => acc + p.overtimeHours, 0),
    doubleTimeHours: previews.reduce((acc, p) => acc + p.doubleTimeHours, 0),
    equipmentHours: rows.reduce((acc, row) => acc + row.equipment.reduce((e, item) => e + item.usageHours, 0), 0),
    estimatedGrossPay: canViewPayroll ? previews.reduce((acc, p) => acc + (p.grossPay ?? 0), 0) : null,
    equipmentCost: previews.reduce((acc, p) => acc + p.equipmentCost, 0),
    exceptionCount: rows.reduce(
      (acc, row) => acc + countRowExceptions(buildRowWarningsForRow(row, options)),
      0,
    ),
  };
}

export const EQUIPMENT_GROUPS = [
  { key: "HEAVY_EQUIPMENT", label: "Heavy Equipment" },
  { key: "VEHICLE", label: "Vehicles" },
  { key: "TOOL", label: "Tools" },
  { key: "RENTAL", label: "Rental" },
  { key: "OTHER", label: "Other" },
] as const;

export function groupEquipment(equipment: DailyEntryEquipmentOption[]) {
  return EQUIPMENT_GROUPS.map((group) => ({
    ...group,
    items: equipment.filter((e) => e.type === group.key),
  })).filter((g) => g.items.length > 0);
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
